import React, { useEffect, useState } from 'react';
import { Button, Card, Popconfirm, Row, Skeleton, Table, Modal, notification } from 'antd';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { DeleteOutlined } from '@ant-design/icons';
import { useIsAuthenticated } from '@azure/msal-react';
import AppealForm from './AppealForm';
import { DELETE_APPEAL, GET_ALL_APPEALS } from './AppealQueries';

const Appeals: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { proceedingId } = useParams<any>();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [appealId, setAppealId] = useState<string | null>(null);
  const [deleteAppeal, { loading: deleting }] = useMutation(DELETE_APPEAL);
  const [getAllAppeals, { data: allAppeals, loading, error, refetch }] =
    useLazyQuery(GET_ALL_APPEALS);

  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    if (tab === '8' && proceedingId) {
      getAllAppeals({
        variables: {
          input: proceedingId,
        },
      });
    }
  }, [tab, proceedingId, getAllAppeals]);

  useEffect(() => {
    if (allAppeals?.appeals?.data) {
      refetch();
    }
  }, [deleteAppeal, refetch]);

  const handleDelete = async (appealId: string) => {
    try {
      await deleteAppeal({
        variables: {
          input: {
            proceedingId: proceedingId,
            appealId: appealId,
          },
        },
      });
      notification.success({
        message: 'Success',
        description: 'Appeal has been successfully deleted.',
      });
      refetch();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: `Error in deleting appeal: ${error.message}`,
      });
    }
  };

  const handleEdit = (appeal: any) => {
    setAppealId(appeal ? appeal.id : null);
    setIsModalVisible(true);
  };

  const closeEditModal = () => {
    setIsModalVisible(false);
    setAppealId(null);
    refetch();
  };

  const appeals = allAppeals?.appeals?.data?.map((appeal: any) => ({
    ...appeal,
    key: appeal.id,
  }));

  const columns: any = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
    },
    {
      title: 'Decision Date',
      dataIndex: 'decisionDate',
      key: 'decisionDate',
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
    },
    {
      title: 'Delete',
      key: 'deleteIcon',
      width: 50,
      onCell: () => ({
        onClick: (event: any) => event.stopPropagation(),
      }),
      render: (_: any, appeal: any) => (
        <Popconfirm
          title="Delete Appeal"
          description="Are you sure to delete this Appeal?"
          onConfirm={() => handleDelete(appeal.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            icon={<DeleteOutlined />}
            size="small"
            style={{ backgroundColor: 'white', color: 'red' }}
          />
        </Popconfirm>
      ),
    },
  ];

  if (loading || deleting) return <Skeleton />;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
      <Row>
        {isAuthenticated && (
          <Button type="primary" onClick={() => handleEdit(null)} style={{ marginLeft: '10px' }}>
            Add Appeal
          </Button>
        )}
      </Row>
      <Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexGrow: 10,
          }}
        >
          <Card
            title={<span className="highlighted-title">Proceeding Appeals</span>}
            style={{ width: '100%', marginTop: '25px' }}
            className="card"
          >
            <Table
              dataSource={appeals}
              loading={loading}
              columns={columns}
              onRow={(record: any) => ({
                onClick: () => handleEdit(record),
                style: { cursor: 'pointer' },
              })}
              rowClassName="clickable-row"
            />
          </Card>
        </div>
      </Row>
      <Modal
        title={appealId ? 'Edit Appeal' : 'Add Appeal'}
        open={isModalVisible}
        onCancel={closeEditModal}
        footer={null}
        destroyOnClose
      >
        <AppealForm
          onCancel={closeEditModal}
          proceedingId={proceedingId}
          appealId={appealId}
          onSuccess={refetch}
        />
      </Modal>
    </div>
  );
};

export default Appeals;
